import { useEffect, useState } from "react";
import { adminTrashList, adminTrashRestore, adminTrashPurge } from "@/lib/api";
import { Loader2, RotateCcw, Trash2, Undo2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const COLL_LABEL = {
  content: "Content", hub: "Knowledge Hub", temples: "Temples", deities: "Deities",
  glossary: "Glossary", services: "Services", posts: "Patrika", users: "Users",
};
const fmt = (s) => (s ? new Date(s).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }) : "—");
const daysLeft = (exp) => { if (!exp) return null; const d = Math.ceil((new Date(exp) - Date.now()) / 86400000); return d < 0 ? 0 : d; };

export default function AdminTrash() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);

  const load = () => adminTrashList().then((d) => { setData(d); setSel(new Set()); });
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const restore = async () => {
    if (!sel.size) return;
    setBusy(true);
    try {
      const res = await adminTrashRestore([...sel]);
      toast.success(`Restored ${res.restored} item${res.restored === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error("Restore failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const purge = async (all) => {
    setBusy(true);
    try {
      const res = await adminTrashPurge(all ? { purge_all: true } : { ids: [...sel] });
      toast.success(`Permanently deleted ${res.purged} item${res.purged === 1 ? "" : "s"}`);
      setConfirmPurge(false);
      load();
    } catch (e) { toast.error("Purge failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  if (!data) return <p className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading trash…</p>;
  const items = data.items || [];

  return (
    <div data-testid="admin-trash-page" className="pb-28">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif-latin text-2xl text-ink">Trash</h1>
          <p className="mt-1 text-sm text-ink-3">Recently deleted pages and users. Items are kept for {data.ttl_days} days, then permanently removed. Restore anything you didn't mean to delete.</p>
        </div>
        {items.length > 0 && (
          <button onClick={() => setConfirmPurge(true)} className="inline-flex items-center gap-2 border border-line px-3 py-2 text-sm text-ink-3 hover:border-red-500 hover:text-red-600" data-testid="trash-empty-btn">
            <Trash2 className="h-4 w-4" /> Empty trash
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="border border-line bg-surface px-4 py-16 text-center text-sm text-ink-3" data-testid="trash-empty">Trash is empty. 🎉</p>
      ) : (
        <div className="border border-line divide-y divide-line" data-testid="trash-list">
          {items.map((it) => {
            const dl = daysLeft(it.expires_at);
            return (
              <label key={it.id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`trash-row-${it.id}`}>
                <input type="checkbox" checked={sel.has(it.id)} onChange={() => toggle(it.id)} className="h-4 w-4 accent-[var(--saffron)]" data-testid={`trash-check-${it.id}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-ink">{it.title}</p>
                  <p className="text-xs text-ink-3">{COLL_LABEL[it.collection] || it.collection}{it.slug ? ` · /${it.slug}` : ""} · deleted {fmt(it.deleted_at)} by {it.deleted_by}</p>
                </div>
                <span className={`shrink-0 text-xs ${dl != null && dl <= 3 ? "text-red-600" : "text-ink-3"}`}>{dl != null ? `${dl}d left` : ""}</span>
              </label>
            );
          })}
        </div>
      )}

      {confirmPurge && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmPurge(false)}>
          <div className="w-full max-w-sm border border-line bg-surface p-6" onClick={(e) => e.stopPropagation()}>
            <p className="flex items-center gap-2 font-serif-latin text-lg text-red-700"><AlertTriangle className="h-5 w-5" /> Empty the trash?</p>
            <p className="mt-2 text-sm text-ink-2">This permanently deletes all {items.length} item{items.length === 1 ? "" : "s"} in the trash. This cannot be undone.</p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmPurge(false)} className="px-4 py-2 text-sm text-ink-2 hover:text-ink">Cancel</button>
              <button onClick={() => purge(true)} disabled={busy} data-testid="trash-purge-confirm" className="inline-flex items-center gap-2 bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Permanently delete
              </button>
            </div>
          </div>
        </div>
      )}

      {sel.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-parchment/95 backdrop-blur md:pl-64">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-5 py-3">
            <span className="text-sm text-ink-2"><span className="font-medium text-ink">{sel.size}</span> selected</span>
            <button onClick={restore} disabled={busy} data-testid="trash-restore-btn" className="ml-auto inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} Restore selected
            </button>
            <button onClick={() => purge(false)} disabled={busy} data-testid="trash-purge-selected" className="inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-3 hover:border-red-500 hover:text-red-600 disabled:opacity-60">
              <Trash2 className="h-4 w-4" /> Delete forever
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
